import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sidebar, ChatPanel, DocumentPanel, ConversationHistoryModal } from "./components";
import {
  Container,
  SidebarSection,
  SectionChatPanel,
  ChatAndDocumentWrap,
  ChatColumn,
  ResizeHandle,
  DocumentPanelSlot,
} from "./HomePageV2.styled";
import { useHomeChat, useHomeChatActions } from "./context";
import {
  MIN_PANEL_WIDTH_PX,
  MAX_CHAT_WIDTH_PX,
  DEFAULT_CHAT_WIDTH_PX,
} from "./utils";

/**
 * Inner component that uses the Context (must be inside the Provider).
 * Handles UI logic and uses Context values.
 */
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const HomePageV2Content = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { registerChatHost, unregisterChatHost } = useHomeChatActions();

  // Home is the native chat page — register as host so the global TopMenu button
  // does not open a second floating ChatSlideOut while the user is already here.
  useEffect(() => {
    registerChatHost({ open: () => {}, close: () => {} });
    return () => unregisterChatHost();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerChatHost, unregisterChatHost]);

  const {
    agents,
    isLoadingAgents,
    selectedAgentId,
    selectAgent,
    clearAgent,
    defaultAgentIdForNewChat,
    defaultAgentForNewChat,
    // Conversations
    conversations,
    selectedConversationId,
    selectConversation,
    clearConversationSelection,
    isDocumentPanelOpen,
    isSidebarCollapsed,
    setSidebarCollapsed,
  } = useHomeChat();

  const [isSearchChatsModalOpen, setIsSearchChatsModalOpen] = useState(false);
  const [chatWidthPx, setChatWidthPx] = useState(DEFAULT_CHAT_WIDTH_PX);
  const [isResizing, setIsResizing] = useState(false);
  const [isHoverResize, setIsHoverResize] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(DEFAULT_CHAT_WIDTH_PX);
  const chatColumnRef = useRef(null);
  const initialAgentAppliedRef = useRef(false);

  // Auto-select agent on initial load: from URL param ?agent=<id> or default (concierge from REACT_APP_CONCIERGE_AGENT_UUID).
  // Only runs once when agents are first available; "New chat" keeps empty selection.
  // If there's already an active conversation (e.g. user navigated here via "Open in Home"
  // from UCP or the global panel), preserve it and skip agent auto-selection entirely.
  useEffect(() => {
    if (isLoadingAgents || agents.length === 0 || initialAgentAppliedRef.current) {
      return;
    }
    if (selectedConversationId) {
      // Active conversation from another page — don't clear it.
      initialAgentAppliedRef.current = true;
      return;
    }
    const agentFromUrl = searchParams.get("agent");
    if (agentFromUrl) {
      const agent = agents.find(
        (a) => a.uuid === agentFromUrl || a.id === agentFromUrl
      );
      if (agent) {
        selectAgent(agentFromUrl, agent);
      } else {
        selectAgent(agentFromUrl);
      }
      const next = new URLSearchParams(searchParams);
      next.delete("agent");
      setSearchParams(next, { replace: true });
    } else if (defaultAgentIdForNewChat && defaultAgentForNewChat) {
      selectAgent(defaultAgentIdForNewChat, defaultAgentForNewChat);
    }
    initialAgentAppliedRef.current = true;
  }, [
    agents,
    isLoadingAgents,
    selectedConversationId,
    searchParams,
    setSearchParams,
    selectAgent,
    defaultAgentIdForNewChat,
    defaultAgentForNewChat,
  ]);

  const handleToggleSidebarCollapse = useCallback(() => {
    setSidebarCollapsed(!isSidebarCollapsed);
  }, [isSidebarCollapsed, setSidebarCollapsed]);

  const handleSelectConversation = useCallback(
    (conversationUuid) => {
      selectConversation(conversationUuid);
    },
    [selectConversation]
  );

  const handleSelectAgent = useCallback(
    (agentId) => {
      // Find agent in list and select it
      const agent = agents.find(
        (a) => a.uuid === agentId || a.id === agentId
      );
      if (agent) {
        selectAgent(agentId, agent);
      } else {
        // If not in list, select by ID only
        selectAgent(agentId);
      }
    },
    [agents, selectAgent]
  );

  const handleNewChat = useCallback(() => {
    setSidebarCollapsed(false);
    const effectiveAgentId = selectedAgentId || defaultAgentIdForNewChat;
    if (effectiveAgentId) {
      const agent =
        agents.find((a) => a.uuid === effectiveAgentId || a.id === effectiveAgentId) ??
        (effectiveAgentId === defaultAgentIdForNewChat ? defaultAgentForNewChat : null);
      selectAgent(effectiveAgentId, agent ?? undefined);
      clearConversationSelection();
    } else {
      clearAgent();
      clearConversationSelection();
    }
  }, [
    selectedAgentId,
    defaultAgentIdForNewChat,
    defaultAgentForNewChat,
    agents,
    selectAgent,
    clearAgent,
    clearConversationSelection,
    setSidebarCollapsed,
  ]);

  const handleSearchChats = useCallback(() => {
    setIsSearchChatsModalOpen(true);
  }, []);
  const lastWidthRef = useRef(chatWidthPx);

  const handleResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      startXRef.current = e.clientX;
      startWidthRef.current = chatWidthPx;
      lastWidthRef.current = chatWidthPx;
      setIsResizing(true);

      const onMove = (moveEvent) => {
        const delta = moveEvent.clientX - startXRef.current;
        const newWidth = clamp(
          startWidthRef.current + delta,
          MIN_PANEL_WIDTH_PX,
          MAX_CHAT_WIDTH_PX
        );
        lastWidthRef.current = newWidth;
        const el = chatColumnRef.current;
        if (el) {
          el.style.width = `${newWidth}px`;
          el.style.flexBasis = `${newWidth}px`;
          el.style.minWidth = `${MIN_PANEL_WIDTH_PX}px`;
          el.style.maxWidth = `${MAX_CHAT_WIDTH_PX}px`;
        } else {
          setChatWidthPx(newWidth);
        }
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        setChatWidthPx(lastWidthRef.current);
        const el = chatColumnRef.current;
        if (el) {
          el.style.width = "";
          el.style.flexBasis = "";
          el.style.minWidth = "";
          el.style.maxWidth = "";
        }
        setIsResizing(false);
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [chatWidthPx]
  );

  return (
    <Container>
      {/* Left sidebar - Agents and Chats navigation */}
      <SidebarSection $isCollapsed={isSidebarCollapsed}>
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={handleToggleSidebarCollapse}
          selectedConversationId={selectedConversationId}
          onSelectConversation={handleSelectConversation}
          selectedAgentId={selectedAgentId}
          onSelectAgent={handleSelectAgent}
          onNewChat={handleNewChat}
          onSearchChats={handleSearchChats}
        />
      </SidebarSection>

      <ConversationHistoryModal
        isOpen={isSearchChatsModalOpen}
        onClose={() => setIsSearchChatsModalOpen(false)}
        conversations={conversations}
        onSelectConversation={handleSelectConversation}
        onNewChat={handleNewChat}
      />

      <SectionChatPanel>
        <ChatAndDocumentWrap>
          <ChatColumn
            ref={chatColumnRef}
            $isDocumentOpen={isDocumentPanelOpen}
            $chatWidthPx={chatWidthPx}
            $isResizing={isResizing}
          >
            <ChatPanel />
          </ChatColumn>
          {isDocumentPanelOpen && (
            <ResizeHandle
              $isHover={isHoverResize}
              $isResizing={isResizing}
              onMouseDown={handleResizeStart}
              onMouseEnter={() => setIsHoverResize(true)}
              onMouseLeave={() => setIsHoverResize(false)}
              role="separator"
              aria-label="Resize chat and document panels"
            />
          )}
          <DocumentPanelSlot $isOpen={isDocumentPanelOpen} $isResizing={isResizing}>
            <DocumentPanel />
          </DocumentPanelSlot>
        </ChatAndDocumentWrap>
      </SectionChatPanel>

      {/* Right panel - Document Editor
      <Section>
        <SectionLabel>DocumentEditor (Document Editor)</SectionLabel>
        <DocumentEditor />
      </Section>
      */}
    </Container>
  );
};

// Provider now lives at App.jsx level (GlobalChatProvider) — no local wrapper needed.
const HomePageV2 = () => <HomePageV2Content />;

export default HomePageV2;
